// src/features/quotation/optimistic.ts
import type { EnrichedQuotationItem, UnitType } from "./types";
import type { SupplierQuotationSubmission } from "../supplierQuotation/types";

/**
 * Builds the set of product:supplier keys that are effectively "already quoted",
 * combining confirmed items with optimistically-pending selections.
 */
export function buildEffectiveQuotedKeys(
  items: { product_id: string; supplier_id: string }[],
  pendingSelections: Set<string>,
): Set<string> {
  const fromItems = items.map((i) => `${i.product_id}:${i.supplier_id}`);
  return new Set([...fromItems, ...pendingSelections]);
}

/**
 * Filters out items that are pending optimistic removal.
 * Roll back by passing an empty (or smaller) pendingRemovals set.
 */
export function buildEffectiveItems<T extends { id: string }>(
  items: T[],
  pendingRemovals: Set<string>,
  pendingDeselections: Set<string> = new Set(),
): T[] {
  if (pendingRemovals.size === 0 && pendingDeselections.size === 0) {
    return items;
  }
  return items.filter((item) => {
    if (pendingRemovals.has(item.id)) return false;
    const key =
      "product_id" in item && "supplier_id" in item
        ? `${item.product_id}:${item.supplier_id}`
        : null;
    return key == null || !pendingDeselections.has(key);
  });
}

export function reconcileOptimisticState(
  items: { id: string; product_id: string; supplier_id: string }[],
  pendingSelections: Set<string>,
  pendingRemovals: Set<string>,
  pendingDeselections: Set<string>,
  exitingIds: Set<string>,
): {
  pendingSelections: Set<string>;
  pendingRemovals: Set<string>;
  pendingDeselections: Set<string>;
  exitingIds: Set<string>;
} {
  const itemIds = new Set(items.map((item) => item.id));
  const itemKeys = new Set(
    items.map((item) => `${item.product_id}:${item.supplier_id}`),
  );

  return {
    pendingSelections: new Set(
      [...pendingSelections].filter((key) => !itemKeys.has(key)),
    ),
    pendingRemovals: new Set(
      [...pendingRemovals].filter((itemId) => itemIds.has(itemId)),
    ),
    pendingDeselections: new Set(
      [...pendingDeselections].filter((key) => itemKeys.has(key)),
    ),
    exitingIds: new Set([...exitingIds].filter((key) => itemKeys.has(key))),
  };
}

// ---------------------------------------------------------------------------
// Stable display list
// ---------------------------------------------------------------------------

export type DetailDisplayItem =
  | (EnrichedQuotationItem & { isSuggestion?: false; isOptimistic?: boolean })
  | {
      id: string;
      product_name: string;
      product_category: string;
      supplier_name: string;
      price_per_box: number;
      price_per_kg?: number;
      price_per_unit: number;
      unit_type: UnitType;
      submission: SupplierQuotationSubmission;
      isSuggestion: true;
    };

/**
 * Builds a stable-ordered grouped display list per category.
 *
 * Submissions always keep their API-response position within their category;
 * only their visual state (confirmed vs suggestion) changes based on
 * effectiveQuotedKeys. Manually-added items (confirmed items with no matching
 * submission) are appended at the end of their category.
 *
 * Both input lists are expected to already be filtered by search query.
 *
 * @precondition Both input lists must use the same filter predicate on the same fields.
 * A confirmed item (key in effectiveQuotedKeys) that is absent from filteredItems but
 * present in filteredSubmissions will be silently omitted. This cannot happen when
 * filteredItems and filteredSubmissions are both filtered by product_name/supplier_name
 * with the same query, which is the only expected usage.
 */
export function buildStableGrouped(
  filteredItems: EnrichedQuotationItem[],
  filteredSubmissions: SupplierQuotationSubmission[],
  effectiveQuotedKeys: Set<string>,
  hiddenSuggestionKeys: Set<string> = new Set(),
): Record<string, DetailDisplayItem[]> {
  // Build a key → confirmed item lookup for O(1) access
  const itemByKey = new Map<string, EnrichedQuotationItem>();
  for (const item of filteredItems) {
    itemByKey.set(`${item.product_id}:${item.supplier_id}`, item);
  }

  const grouped: Record<string, DetailDisplayItem[]> = {};

  // Pass 1: submissions in stable API order — render confirmed or suggestion
  for (const submission of filteredSubmissions) {
    const key = `${submission.product_id}:${submission.supplier_id}`;
    const cat = submission.product_category;
    if (effectiveQuotedKeys.has(key)) {
      const bucket = (grouped[cat] ??= []);
      const confirmedItem = itemByKey.get(key);
      if (confirmedItem) {
        bucket.push(confirmedItem);
      } else {
        bucket.push({
          id: `optimistic-${submission.id}`,
          quotation_id: submission.quotation_id,
          product_id: submission.product_id,
          supplier_id: submission.supplier_id,
          product_name: submission.product_name,
          product_category: submission.product_category,
          supplier_name: submission.supplier_name,
          price_per_box: submission.price_per_box,
          price_per_kg: submission.price_per_kg,
          price_per_unit: submission.price_per_unit,
          unit_type: submission.unit_type as UnitType,
          is_offer: false,
          created_at: "",
          created_by: "",
          updated_at: "",
          updated_by: "",
          updated_by_name: "",
          isOptimistic: true,
        });
      }
    } else if (hiddenSuggestionKeys.has(key)) {
      continue;
    } else {
      const bucket = (grouped[cat] ??= []);
      bucket.push({
        id: `submission-${submission.id}`,
        product_name: submission.product_name,
        product_category: submission.product_category,
        supplier_name: submission.supplier_name,
        price_per_box: submission.price_per_box,
        price_per_kg: submission.price_per_kg,
        price_per_unit: submission.price_per_unit,
        unit_type: submission.unit_type as UnitType,
        submission,
        isSuggestion: true,
      });
    }
  }

  // Pass 2: manually-added items (no matching submission) → append per category
  const submissionKeySet = new Set(
    filteredSubmissions.map((s) => `${s.product_id}:${s.supplier_id}`),
  );
  for (const item of filteredItems) {
    const key = `${item.product_id}:${item.supplier_id}`;
    if (!submissionKeySet.has(key)) {
      const cat = item.product_category;
      (grouped[cat] ??= []).push(item);
    }
  }

  return grouped;
}
